import { accountEntityName } from '../../../../backend/features/accounts/shared';
import { useContactManagementTranslation } from '../../../../ui/hooks/useContactManagementTranslation';
import { TextCell } from '@juvare/common-data-table-ui/DataTable';
import { useDynamicColumnDefinitions } from '@juvare/platform-sdk-react/ui/components/EntityTable/dynamicColumns';
import { buildDynamicColumns } from '@juvare/platform-sdk-react/ui/features/dynamic-columns';
import { getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { Typography } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { getOrgRole } from '../../organizations/components/OrganizationsPanel/utils/getOrgRole';
import { getOrgStatus } from '../../organizations/components/OrganizationsPanel/utils/getOrgStatus';
import { AccountStatusChip } from '../components/AccountStatusChip';
export function useAccountColumns({ showAccountStatus, staticOrganizationId }) {
    const { t } = useContactManagementTranslation();
    const settings = useSelector(getSettings);
    const { data: dynamicColumnData } = useDynamicColumnDefinitions(accountEntityName, settings.dynamicColumnsEnabled); // TODO handle dynamic filter loading and error
    return React.useMemo(() => {
        const accountColumns = [
            {
                headerLabel: t('panels.accounts.columnLabels.name'),
                sortKey: 'displayName',
                cellFunction: (row) => (React.createElement(TextCell, null,
                    React.createElement(Typography, { style: { wordWrap: 'break-word' } }, row.columns.displayName),
                    React.createElement(Typography, { color: 'textSecondary', variant: 'body2', noWrap: true }, row.columns.email))),
                tableCellSx: {
                    display: {
                        xs: 'table-cell',
                        sm: 'none',
                    },
                },
            },
            {
                headerLabel: t('panels.accounts.columnLabels.lastName'),
                sortKey: 'lastName',
                cellFunction: (row) => React.createElement(TextCell, null, row.columns.lastName),
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            {
                headerLabel: t('panels.accounts.columnLabels.firstName'),
                sortKey: 'firstName',
                cellFunction: (row) => React.createElement(TextCell, null, row.columns.firstName),
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            {
                headerLabel: t('panels.accounts.columnLabels.email'),
                sortKey: 'email',
                cellFunction: (row) => React.createElement(TextCell, null, row.columns.email),
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            },
            ...(staticOrganizationId
                ? [
                    {
                        headerLabel: t('forms.orgMembers.status'),
                        sortKey: 'orgStatus',
                        cellFunction: (row) => (React.createElement(TextCell, null, getOrgStatus(row.columns.orgStatus, settings.orgStatusMap, staticOrganizationId, t))),
                        tableCellSx: {
                            display: {
                                xs: 'none',
                                sm: 'table-cell',
                            },
                        },
                    },
                    {
                        headerLabel: t('forms.orgMembers.role'),
                        sortKey: 'orgRole',
                        cellFunction: (row) => (React.createElement(TextCell, null, getOrgRole(row.columns.orgRole, settings.orgRoleMap, staticOrganizationId))),
                        tableCellSx: {
                            display: {
                                xs: 'none',
                                sm: 'table-cell',
                            },
                        },
                    },
                ]
                : []),
            ...(staticOrganizationId
                ? []
                : [
                    {
                        headerLabel: t('panels.accounts.columnLabels.systemRole'),
                        sortKey: 'systemRole',
                        cellFunction: (row) => { var _a, _b; return React.createElement(TextCell, null, (_b = (_a = settings.systemRoleMap[row.columns.systemRole]) === null || _a === void 0 ? void 0 : _a.label) !== null && _b !== void 0 ? _b : 'Unknown'); },
                        tableCellSx: {
                            display: {
                                xs: 'none',
                                sm: 'table-cell',
                            },
                        },
                    },
                ]),
        ];
        if (showAccountStatus || staticOrganizationId) {
            accountColumns.push({
                headerLabel: t('panels.accounts.columnLabels.systemStatus'),
                sortKey: 'systemStatus',
                cellFunction: (row) => (React.createElement(AccountStatusChip, { status: row.columns.systemStatus, label: t(`panels.accounts.status.${row.columns.systemStatus}`) })),
                tableCellSx: {
                    display: {
                        xs: 'none',
                        sm: 'table-cell',
                    },
                },
            });
        }
        accountColumns.push(...buildDynamicColumns(dynamicColumnData));
        return accountColumns;
    }, [t, staticOrganizationId, showAccountStatus, dynamicColumnData, settings.orgStatusMap, settings.orgRoleMap, settings.systemRoleMap]);
}
